import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { setCircularLoading } from "../../../reducers/UiReducer";
import { getService } from "../../../reducers/service";
import { serviceNames } from "../../../utils/constants";
import { notify } from "../../../reducers/NotifierReducer";
import { PATH_TRAINING_PARTICIPANTS } from "../../../utils/routeConstants";
import { mapDictionaryOptions } from "../../../utils/functions";
import { dateTimePicker } from "../../../reducers/TimeReducer";

export const useParticipantData = (id, reset, courseId = null) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userAgency = useSelector((state) => state.user.userAgency);
  const [loaded, setLoaded] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [courses, setCourses] = useState([]);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        dispatch(setCircularLoading(true));
        setLoaded(false);

        const services = {
          distinctService: getService(serviceNames.trainingCourseDistinct),
          employees: getService(serviceNames.personnel),
          courses: getService(serviceNames.trainingCourse),
          participants: getService(serviceNames.perTraining),
        };

        const results = await Promise.all([
          services.distinctService.find({
            query: { column: ["location", "category"], agencyId: userAgency },
          }),
          services.employees.find({
            query: {
              $limit: -1,
              isDeleted: false,
              $sort: { lastName: 1, firstName: 1, middleName: 1 },
              agencyId: userAgency,
            },
          }),
          services.courses.find({
            query: { $limit: -1, isDeleted: false, agencyId: userAgency },
          }),
        ]);

        let filteredEmployees = results[1];

        if (!id && courseId) {
          const participants = await services.participants.find({
            query: { perTrainingCourseId: courseId, $limit: -1 },
          });

          const existingEmployeeNums = new Set(
            participants.map((p) => p.employeeNum)
          );

          filteredEmployees = results[1].filter(
            (emp) => !existingEmployeeNums.has(emp.employeeNum)
          );
        }

        if (isMounted) {
          setLocationOptions(results[0]?.location);
          setCategoryOptions(results[0]?.category);
          setEmployees(filteredEmployees);
          setCourses(results[2]);
        }

        if (id) {
          const service = getService(serviceNames.perTraining);

          const resultData = await service.find({
            query: {
              id: id,
              agencyId: userAgency,
            },
          });

          const result = resultData?.data?.[0] || null;

          if (!result) {
            setTimeout(() => {
              dispatch(notify("You don't have access to this participant.", "warning"));
            }, 1000);
            history.go(-1);
            return;
          }

          if (isMounted && result) {
            reset({
              id: result.id || null,
              employeeNum: result.employeeNum || "",
              courseNumber: result.courseNumber || "",
              course: result.course || "",
              courseDate: result.courseDate
                ? dateTimePicker(result.courseDate)
                : "",
              attendedDate: result.attendedDate
                ? dateTimePicker(result.attendedDate)
                : "",
              courseLocation: result.courseLocation || "",
              dateRegistered: result.dateRegistered
                ? dateTimePicker(result.dateRegistered)
                : "",
              hours: result.hours || "",
              grade: result.grade || "",
              approved: result.approved || false,
              checkSent: result.checkSent || false,
              dateCheckSent: result.dateCheckSent
                ? dateTimePicker(result.dateCheckSent)
                : "",
              category: result.category || "",
              approvedBy: result.approvedBy || "",
              comments: result.comments || "",
              participantNumber: result.participantNumber || null,
            });
          }
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          dispatch(notify("Failed to fetch training participant", "error"));
          history.push(PATH_TRAINING_PARTICIPANTS);
        }
      } finally {
        dispatch(setCircularLoading(false));
        setLoaded(true);
      }
    };

    init();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch, reset, userAgency]);

  return {
    loaded,
    locationOptions,
    categoryOptions,
    employees,
    courses,
    tagTypeOptions,
  };
};
